import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Video player for uploaded video files.
 *
 * Two things shape this:
 *
 * - Nothing is downloaded until the visitor asks for it. A poster image is shown
 *   and `preload="none"` is set, because gallery originals can be up to 500MB and
 *   a grid of them would otherwise be ruinous on a phone.
 * - The scrubber is driven by requestAnimationFrame rather than the `timeupdate`
 *   event, which only fires roughly four times a second and makes the progress bar
 *   visibly step instead of glide.
 */
export default function VideoPlayer({
    src,
    poster,
    className = '',
    style,
    loop = false,
    muted = false,
    showControls = true,
    onEnded,
}) {
    const videoRef = useRef(null);
    const frameRef = useRef(null);
    const [started, setStarted] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    // Smooth progress: one sample per painted frame while playing.
    const tick = useCallback(() => {
        const video = videoRef.current;
        if (video && video.duration > 0) {
            setProgress((video.currentTime / video.duration) * 100);
        }
        frameRef.current = requestAnimationFrame(tick);
    }, []);

    useEffect(() => {
        if (playing) {
            frameRef.current = requestAnimationFrame(tick);
        } else if (frameRef.current) {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }

        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        };
    }, [playing, tick]);

    const play = useCallback(() => {
        setStarted(true);
        const video = videoRef.current;
        if (!video) return;
        video.play().catch(() => setPlaying(false));
    }, []);

    const toggle = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) play();
        else video.pause();
    }, [play]);

    const seek = useCallback((event) => {
        const video = videoRef.current;
        if (!video || !video.duration) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
        video.currentTime = ratio * video.duration;
        setProgress(ratio * 100);
    }, []);

    return (
        <div className={`relative overflow-hidden ${className}`} style={style}>
            <video
                ref={videoRef}
                src={src}
                poster={poster || undefined}
                // Nothing is fetched until the visitor presses play.
                preload={started ? 'metadata' : 'none'}
                playsInline
                loop={loop}
                muted={muted}
                className="block w-full h-full object-cover"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); onEnded?.(); }}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
                onClick={showControls ? toggle : undefined}
            />

            {!started && (
                <button
                    type="button"
                    onClick={play}
                    className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
                    aria-label="Play video"
                >
                    <span className="flex items-center justify-center w-14 h-14 rounded-full bg-white/85 shadow">
                        <svg className="w-6 h-6 ml-0.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </span>
                </button>
            )}

            {showControls && started && (
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/50 to-transparent">
                    <div
                        className="h-1 bg-white/30 rounded cursor-pointer"
                        onClick={seek}
                        role="slider"
                        tabIndex={0}
                        aria-label="Seek"
                        aria-valuenow={Math.round(progress)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        onKeyDown={(e) => {
                            const video = videoRef.current;
                            if (!video?.duration) return;
                            if (e.key === 'ArrowRight') video.currentTime = Math.min(video.currentTime + 5, video.duration);
                            if (e.key === 'ArrowLeft') video.currentTime = Math.max(video.currentTime - 5, 0);
                        }}
                    >
                        {/* Width is transform-free so the browser can update it per frame cheaply. */}
                        <div className="h-full bg-white rounded" style={{ width: `${progress}%` }} />
                    </div>
                    {duration > 0 && (
                        <div className="mt-1 text-[10px] text-white/80 tabular-nums">
                            {formatTime((progress / 100) * duration)} / {formatTime(duration)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function formatTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00';
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
}

VideoPlayer.propTypes = {
    src: PropTypes.string.isRequired,
    poster: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    loop: PropTypes.bool,
    muted: PropTypes.bool,
    showControls: PropTypes.bool,
    onEnded: PropTypes.func,
};
